/**
 * @file BackToTop.tsx
 * @description "Back to Top" button component that scrolls the page to the top.
 * Features an animated chevron icon and CMS-managed accessible label.
 */

'use client';

import { backToTopVariants } from './BackToTop.styles';
import { useGlobalLabels } from '@/context/GlobalLabelContext';
import { getCSLPAttributes } from '@/utils/type-guards';
import { trackAdobeClick } from '@/utils/adobe/click';
import { IStandardContentPage } from '@/.generated';

/**
 * Back to Top button component.
 * Provides a link to skip back to the top of the page.
 * Animates the chevron icon upward on hover.
 *
 * @returns {JSX.Element} Back to top button component
 */
export const BackToTop = (props: NonNullable<IStandardContentPage['back_to_top']>) => {
  const { globalLabels } = useGlobalLabels();
  const fallbackText = 'Back to top';

  const { container, link, icon, text } = backToTopVariants();

  if (!props.enable_back_to_top) return null;
  return (
    <div className={container()}>
      <a
        href="#page-content"
        className={link()}
        aria-label={globalLabels.back_to_top_label || fallbackText}
        onClick={() =>
          trackAdobeClick({
            clickName: globalLabels.back_to_top_label || fallbackText,
            clickType: 'pagetop-click',
            clickLocation: 'backToTop',
            clickUrl: 'pagetop-click',
          })
        }
        {...getCSLPAttributes(globalLabels.$?.back_to_top_label)}
      >
        <i className={icon()} aria-hidden="true" />

        <p className={text()}>{globalLabels.back_to_top_label || fallbackText}</p>
      </a>
    </div>
  );
};
